import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Site } from 'common/models/Site';
import SitePicker from 'components/Pickers/SitePicker';
import React, { useCallback, useEffect, useState } from 'react';
import { ChangePlacementSite } from 'services/PlacementsService';

interface Props {
  open: boolean;
  placementId: number;
  siteId: number;
  clientId: number;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  successHandler?: (message: string, selectedSite: Site | null) => void;
  errorHandler?: (message: string) => void;
}

export default function ChangeSiteDialog({
  open,
  placementId,
  siteId: siteIdProp,
  clientId,
  closeHandler,
  loadingHandler,
  successHandler,
  errorHandler,
}: Props) {
  const [siteId, setSiteId] = useState(0);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  useEffect(() => {
    if (!open) {
      setSiteId(siteIdProp);
      setSelectedSite(null);
    }
  }, [open, siteIdProp]);

  const submitCallback = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    const res = await ChangePlacementSite(placementId, siteId, errorHandler);
    if (res) {
      successHandler && successHandler('Site Changed', selectedSite);
      closeHandler();
    }
    loadingHandler && loadingHandler(false);
  }, [
    closeHandler,
    errorHandler,
    loadingHandler,
    placementId,
    selectedSite,
    siteId,
    successHandler,
  ]);

  const siteChangeHandler = useCallback((s: Site | null) => {
    setSiteId(s ? s.id : 0);
    setSelectedSite(s);
  }, []);

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle>Change Site</DialogTitle>
      <DialogContent>
        <DialogContentText textAlign="justify" mb={2}>
          If you are integrated with a Financial system, Site is linked to GL Accounts, Payroll
          taxes etc. If you need to update the site to be in a different state, we strongly
          recommend early terminating this placement instead & creating a new placement
        </DialogContentText>
        <SitePicker value={siteId} clientId={clientId} onSelectCallback={siteChangeHandler} />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={siteId === siteIdProp || siteId === 0}
          onClick={submitCallback}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
